import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Box,
  IconButton,
  FormControlLabel,
  Switch,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Tooltip,
} from "@mui/material";
import CommonModal from "../common/CommonModal";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import {
  ApplicationService,
  ApplicationServiceRequest,
  ApplicationServiceParameter,
  INPUT_TYPES,
  InputType,
} from "../../hooks/useApplicationServices";

interface ServiceFormModalProps {
  open: boolean;
  handleClose: () => void;
  onSubmit: (formData: ApplicationServiceRequest & { id?: number }) => void;
  isSubmitting?: boolean;
  isEdit?: boolean;
  initialData?: ApplicationService;
}

const emptyParameter = (): ApplicationServiceParameter => ({
  parameterKey: "",
  label: "",
  inputType: "TEXT",
  required: false,
  placeholder: "",
});

const ServiceFormModal: React.FC<ServiceFormModalProps> = ({
  open,
  handleClose,
  onSubmit,
  isSubmitting = false,
  isEdit = false,
  initialData,
}) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState<ApplicationServiceRequest & { id?: number }>({
    code: "",
    name: "",
    applicationServiceParameter: [],
    active: true,
  });

  useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        setFormData({
          id: initialData.id,
          code: initialData.code || "",
          name: initialData.name || "",
          applicationServiceParameter: initialData.applicationServiceParameter ?? [],
          active: initialData.active ?? true,
        });
      } else {
        setFormData({ code: "", name: "", applicationServiceParameter: [], active: true });
      }
    }
  }, [open, isEdit, initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...(isEdit && formData.id != null ? { id: formData.id } : {}),
      code: formData.code.trim().toUpperCase(),
      name: formData.name.trim(),
      applicationServiceParameter: formData.applicationServiceParameter ?? [],
      active: formData.active ?? true,
    });
  };

  const params = formData.applicationServiceParameter ?? [];

  const handleAddParam = () => {
    setFormData((prev) => ({
      ...prev,
      applicationServiceParameter: [...(prev.applicationServiceParameter ?? []), emptyParameter()],
    }));
  };

  const handleRemoveParam = (index: number) => {
    setFormData((prev) => {
      const updated = [...(prev.applicationServiceParameter ?? [])];
      updated.splice(index, 1);
      return { ...prev, applicationServiceParameter: updated };
    });
  };

  const handleParamChange = (
    index: number,
    field: keyof ApplicationServiceParameter,
    value: any
  ) => {
    setFormData((prev) => {
      const updated = [...(prev.applicationServiceParameter ?? [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, applicationServiceParameter: updated };
    });
  };

  const isValid = !!formData.code.trim() && !!formData.name.trim();

  return (
    <CommonModal
      open={open}
      onClose={handleClose}
      title={isEdit ? t("edit_service") : t("add_service")}
      maxWidth="md"
      customActions={
        <>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={isSubmitting}
            sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
          >
            {t("cancel")}
          </Button>
          <Button
            form="service-form"
            type="submit"
            variant="contained"
            disabled={!isValid || isSubmitting}
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
          >
            {isSubmitting ? t("saving") : isEdit ? t("update") : t("add")}
          </Button>
        </>
      }
    >
      <form id="service-form" onSubmit={handleSubmit}>
          {/* Basic Info */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField
                label={t("service_code")}
                value={formData.code}
                onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value }))}
                required
                fullWidth
                inputProps={{ style: { textTransform: "uppercase" } }}
              />
              <TextField
                label={t("service_name")}
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                required
                fullWidth
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active ?? true}
                  onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.checked }))}
                />
              }
              label={t("active")}
            />
          </Box>

          {/* Parameters Section */}
          <Divider sx={{ my: 2.5 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Parameters
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ({params.length})
              </Typography>
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddParam}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Add Parameter
            </Button>
          </Box>

          {params.length === 0 ? (
            <Box sx={{ py: 3, textAlign: "center", bgcolor: "#f9fafb", borderRadius: 2, border: "1px dashed #d0d5dd" }}>
              <Typography variant="body2" color="text.secondary">
                No parameters yet. Click "Add Parameter" to add one.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {/* Header */}
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 130px 1fr 80px 40px", gap: 1, px: 1 }}>
                {["Parameter Key", "Label", "Input Type", "Placeholder", "Required", ""].map((h) => (
                  <Typography key={h} variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", fontSize: "0.7rem" }}>
                    {h}
                  </Typography>
                ))}
              </Box>

              {params.map((param, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 130px 1fr 80px 40px",
                    gap: 1,
                    alignItems: "center",
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid #e5e7eb",
                    bgcolor: "#fafafa",
                    "&:hover": { borderColor: "#3092e3", bgcolor: "#f5f8ff" },
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="e.g. api_key"
                    value={param.parameterKey}
                    onChange={(e) => handleParamChange(idx, "parameterKey", e.target.value)}
                  />
                  <TextField
                    size="small"
                    placeholder="e.g. API Key"
                    value={param.label}
                    onChange={(e) => handleParamChange(idx, "label", e.target.value)}
                  />
                  <FormControl size="small" fullWidth>
                    <Select
                      value={param.inputType}
                      onChange={(e) => handleParamChange(idx, "inputType", e.target.value as InputType)}
                    >
                      {INPUT_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    placeholder="Placeholder text"
                    value={param.placeholder}
                    onChange={(e) => handleParamChange(idx, "placeholder", e.target.value)}
                  />
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Switch
                      size="small"
                      checked={param.required}
                      onChange={(e) => handleParamChange(idx, "required", e.target.checked)}
                    />
                  </Box>
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveParam(idx)}
                      sx={{ bgcolor: "#fff5f5", "&:hover": { bgcolor: "#ffeaea" } }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          )}
      </form>
    </CommonModal>
  );
};

export default ServiceFormModal;
